/**
 * Public demo-world endpoint — NO AUTH. The anonymous /demo route reads one
 * persona's PUBLISHED world (a read-only projection: no private data, respects
 * the published flag) so a visitor can wander a real, populated Diwa before
 * signing up.
 *
 * Shared contract (backend implements; frontend codes to it) — MULTI-WORLD:
 *   GET /api/diwa/public/demo        -> { worlds:[{key, persona:{name,tagline}}] }  (the picker menu)
 *   GET /api/diwa/public/demo/{key}  -> { persona:{name,tagline}, domains:[…],
 *                                         graph:{nodes,edges}, signals:[…] }        (one world)
 *
 * This module carries the read-only shape, a pure entity→constellation mapper
 * (unit-tested, no DOM), and seeded stubs so /demo renders even if the endpoint
 * is unreachable. Every field here is display-only — the client never writes.
 */

import type { ConstellationEdge, ConstellationNode } from '@diwata/ironvale'
import { AE, EGO, starchartColorMap } from '@/lib/graphColors'

// ── The read-only projection (mirrors the backend's public payload) ──────────

export interface DemoPersona {
  name: string
  tagline: string
}

export interface DemoDomain {
  slug: string
  name: string
  /** One-line gloss of what this region is. Optional — the legend degrades gracefully. */
  description?: string
}

export interface DemoNode {
  id: string
  name: string
  /** Entity kind — person / company / event / work / … (free-form, display only). */
  type: string
  /** The domain slug this star belongs to (its sector + hue). */
  domain: string
  /** The persona themself — the ego at the centre of the sky. At most one. */
  self?: boolean
}

export interface DemoEdge {
  source: string
  target: string
  /** Relation label shown on the golden thread when hovered. */
  type: string
}

export interface DemoSignal {
  id: string
  title: string
  domain: string
  /** How loud — high / medium / low. Display only. */
  importance?: string
  /** The warm path Diwa would suggest — read-only here (no action button). */
  suggested_action?: string
}

export interface DemoWorld {
  persona: DemoPersona
  domains: DemoDomain[]
  graph: { nodes: DemoNode[]; edges: DemoEdge[] }
  signals: DemoSignal[]
}

/** One entry in the picker menu — a world's stable key + its persona headline. */
export interface DemoWorldSummary {
  key: string
  persona: DemoPersona
}

const base = '/api/diwa'

/**
 * The picker menu — every published demo world's key + persona. No token, no
 * credentials — a truly public read. The full world for a chosen key comes from
 * `fetchDemoWorld(key)`.
 */
export async function fetchDemoWorlds(): Promise<DemoWorldSummary[]> {
  const r = await fetch(`${base}/public/demo`)
  if (!r.ok) throw new Error(`public demo list: ${r.status}`)
  const body = (await r.json()) as { worlds?: DemoWorldSummary[] }
  return body.worlds ?? []
}

/** Fetch ONE published demo world by key. No token, no credentials — a public read. */
export async function fetchDemoWorld(key: string): Promise<DemoWorld> {
  const r = await fetch(`${base}/public/demo/${encodeURIComponent(key)}`)
  if (!r.ok) throw new Error(`public demo ${key}: ${r.status}`)
  return r.json()
}

/**
 * A short role for the picker — the head of the persona's tagline (before the em dash),
 * e.g. "Markets analyst at Aurelian Capital". Falls back to the whole tagline.
 */
export function personaRole(p: DemoPersona): string {
  return (p.tagline.split('—')[0] ?? p.tagline).trim()
}

// ── Pure mapping: demo entities/relations → the ironvale Constellation model ──

/**
 * One stable hue per domain, over the union of declared domains ∪ node domains.
 * The public /demo sky speaks the STAR-CHART palette — the same four separated
 * hues as the marketing hero (semantic where a slug is recognisable: govcon is
 * always burnished gold, media always ember copper), so the whole public
 * surface reads as one instrument.
 */
export function demoDomainColors(world: DemoWorld): Map<string, string> {
  return starchartColorMap([
    ...world.domains.map((d) => d.slug),
    ...world.graph.nodes.map((n) => n.domain),
  ])
}

/** The ego node id (the persona) if the world names one. */
export function egoIdOf(world: DemoWorld): string | null {
  return world.graph.nodes.find((n) => n.self)?.id ?? null
}

/**
 * Map the demo graph onto the primitive's generic node/edge model. The ego wears
 * its crimson core (via egoId in the primitive); every other star is a parchment
 * core with its domain hue as the halo. A `focus` set (lit domains) brightens the
 * chosen regions and dims the rest — the read-only "legend focus".
 */
export function toConstellationNodes(
  world: DemoWorld,
  colors: Map<string, string>,
  focus?: Set<string>,
): ConstellationNode[] {
  const focusing = !!focus && focus.size > 0
  const lit = (domain: string) => !focusing || focus!.has(domain)
  return world.graph.nodes.map((n) => ({
    id: n.id,
    label: n.name,
    group: n.domain,
    // a dimmed region loses its hue too — focus reads as light AND colour
    glow: n.self ? EGO.halo : lit(n.domain) ? colors.get(n.domain) ?? AE.ash : AE.ash,
    weight: n.self ? 26 : 11,
    // focus is a real spotlight: chosen regions burn brighter than the resting
    // sky ever does, everything else recedes to embers (founder: the old 0.82 vs
    // 0.16 barely registered on a click)
    brightness: n.self ? 1 : lit(n.domain) ? (focusing ? 1 : 0.82) : 0.08,
    meta: n.type,
  }))
}

export function toConstellationEdges(world: DemoWorld): ConstellationEdge[] {
  return world.graph.edges.map((e) => ({
    from: e.source,
    to: e.target,
    label: e.type,
    kind: 'confirmed',
  }))
}

/**
 * A relation is *cross-domain* when its two endpoints live in different domains —
 * the "one connected world" seam. Returns endpoint ids so the tour can point at one.
 */
export function crossDomainEdges(world: DemoWorld): DemoEdge[] {
  const domainOf = new Map(world.graph.nodes.map((n) => [n.id, n.domain]))
  return world.graph.edges.filter((e) => {
    const a = domainOf.get(e.source)
    const b = domainOf.get(e.target)
    return a !== undefined && b !== undefined && a !== b
  })
}

export interface DemoRelation {
  /** The name of the entity on the other end of the thread. */
  name: string
  /** The entity kind on the other end. */
  type: string
  /** The domain on the other end (may differ — a cross-domain thread). */
  domain: string
  /** The relation label. */
  relation: string
  /** True when the other end lives in a different domain. */
  crossDomain: boolean
}

/**
 * Every thread touching one star, resolved to the entity on the far end — the
 * read-only peek's "relations" list. Undirected: an edge counts from either side.
 */
export function relationsOf(world: DemoWorld, id: string): DemoRelation[] {
  const byId = new Map(world.graph.nodes.map((n) => [n.id, n]))
  const self = byId.get(id)
  if (!self) return []
  const out: DemoRelation[] = []
  for (const e of world.graph.edges) {
    const otherId = e.source === id ? e.target : e.target === id ? e.source : null
    if (!otherId) continue
    const other = byId.get(otherId)
    if (!other) continue
    out.push({
      name: other.name,
      type: other.type,
      domain: other.domain,
      relation: e.type,
      crossDomain: other.domain !== self.domain,
    })
  }
  return out
}

// ── Seeded stub — a real-feeling published world for offline render/screenshot ─
// Used only as a fallback when the public endpoint isn't reachable, so the demo
// page is always a live, populated example. The persona: an analyst-founder whose
// four domains (markets, research, network, library) are genuinely cross-linked.

export const DEMO_STUB: DemoWorld = {
  persona: {
    name: 'Maya Restos',
    tagline: 'Analyst-founder mapping AI markets, the people behind them, and what she reads.',
  },
  domains: [
    { slug: 'markets', name: 'Markets Watch', description: 'Companies and moves she tracks.' },
    { slug: 'research', name: 'AI Research', description: 'Papers, labs, and results that matter.' },
    { slug: 'network', name: 'People & Orgs', description: 'The humans in her orbit.' },
    { slug: 'library', name: 'Library', description: 'What she is reading and why.' },
  ],
  graph: {
    nodes: [
      { id: 'ego', name: 'Maya', type: 'you', domain: 'network', self: true },
      // markets
      { id: 'm-nvidia', name: 'Nvidia', type: 'company', domain: 'markets' },
      { id: 'm-anthropic', name: 'Anthropic', type: 'company', domain: 'markets' },
      { id: 'm-perplexity', name: 'Perplexity', type: 'company', domain: 'markets' },
      { id: 'm-raise', name: 'Series C — $200M', type: 'event', domain: 'markets' },
      // research
      { id: 'r-scaling', name: 'Scaling Laws v3', type: 'paper', domain: 'research' },
      { id: 'r-agents', name: 'Long-horizon Agents', type: 'paper', domain: 'research' },
      { id: 'r-neurips', name: 'NeurIPS 2026', type: 'event', domain: 'research' },
      { id: 'r-lab', name: 'Frontier Lab', type: 'org', domain: 'research' },
      // network
      { id: 'n-devon', name: 'Devon Cruz', type: 'person', domain: 'network' },
      { id: 'n-priya', name: 'Priya Nair', type: 'person', domain: 'network' },
      { id: 'n-jacob', name: 'Jacob Barak', type: 'person', domain: 'network' },
      { id: 'n-fund', name: 'Lattice Capital', type: 'org', domain: 'network' },
      // library
      { id: 'l-highgrowth', name: 'High Growth Handbook', type: 'book', domain: 'library' },
      { id: 'l-thinking', name: 'Thinking in Systems', type: 'book', domain: 'library' },
      { id: 'l-essay', name: 'On Agentic Loops', type: 'essay', domain: 'library' },
    ],
    edges: [
      // ego reach
      { source: 'ego', target: 'n-devon', type: 'partner' },
      { source: 'ego', target: 'n-priya', type: 'colleague' },
      { source: 'ego', target: 'm-anthropic', type: 'tracks' },
      { source: 'ego', target: 'l-thinking', type: 'reading' },
      // within markets
      { source: 'm-perplexity', target: 'm-raise', type: 'raised' },
      { source: 'm-nvidia', target: 'm-anthropic', type: 'supplies' },
      // within research
      { source: 'r-lab', target: 'r-scaling', type: 'published' },
      { source: 'r-lab', target: 'r-agents', type: 'published' },
      { source: 'r-agents', target: 'r-neurips', type: 'presented at' },
      // within network
      { source: 'n-devon', target: 'n-fund', type: 'works at' },
      { source: 'n-jacob', target: 'n-fund', type: 'advises' },
      // ── cross-domain threads (the connected world) ──
      { source: 'n-priya', target: 'r-scaling', type: 'authored' }, // network → research
      { source: 'm-anthropic', target: 'r-lab', type: 'partners with' }, // markets → research
      { source: 'r-agents', target: 'l-essay', type: 'inspired' }, // research → library
      { source: 'n-devon', target: 'm-perplexity', type: 'angel in' }, // network → markets
      { source: 'l-highgrowth', target: 'm-raise', type: 'referenced by' }, // library → markets
      { source: 'n-jacob', target: 'r-neurips', type: 'speaking at' }, // network → research
    ],
  },
  signals: [
    {
      id: 's1',
      title: 'Perplexity closed its Series C — a name in your Markets domain moved.',
      domain: 'markets',
      importance: 'high',
      suggested_action: 'Open a mission to brief the round and who led it.',
    },
    {
      id: 's2',
      title: 'Priya Nair authored a new Scaling Laws paper — your network touches your research.',
      domain: 'research',
      importance: 'medium',
      suggested_action: 'Draft a note to Priya congratulating her.',
    },
    {
      id: 's3',
      title: 'Jacob Barak is speaking at NeurIPS 2026 — a warm path into the conference.',
      domain: 'network',
      importance: 'medium',
      suggested_action: 'Ask Jacob for an intro to the organisers.',
    },
  ],
}

// A second, wholly NON-FINANCE fallback world — media & research — so the picker still
// offers two worlds when the endpoint is unreachable. Mirrors the backend's Ana spec.
export const DEMO_STUB_ANA: DemoWorld = {
  persona: {
    name: 'Ana Vireo',
    tagline: 'Media & research curator — tracing how creators, scores, studios and the papers about them all connect.',
  },
  domains: [
    { slug: 'anime-shelf', name: 'Anime / Library', description: 'Series, films and studios.' },
    { slug: 'music-desk', name: 'Music', description: 'Composers, albums and the score layer.' },
    { slug: 'papers-lab', name: 'Papers / Research', description: 'Papers, venues and what they study.' },
    { slug: 'creators-crm', name: 'Creators / People', description: 'The people who make and study the work.' },
  ],
  graph: {
    nodes: [
      { id: 'a-ego', name: 'Ana Vireo', type: 'person', domain: 'creators-crm', self: true },
      // anime
      { id: 'a-aster', name: 'Aster Field', type: 'series', domain: 'anime-shelf' },
      { id: 'a-lumen', name: 'Studio Lumen', type: 'studio', domain: 'anime-shelf' },
      { id: 'a-saga', name: 'Aster Saga', type: 'franchise', domain: 'anime-shelf' },
      // music
      { id: 'a-kaito', name: 'Kaito Mori', type: 'composer', domain: 'music-desk' },
      { id: 'a-theme', name: 'Aster Theme', type: 'track', domain: 'music-desk' },
      { id: 'a-nocturne', name: 'Nocturne Drift', type: 'album', domain: 'music-desk' },
      // papers
      { id: 'a-paper', name: 'Animating Emotion', type: 'paper', domain: 'papers-lab' },
      { id: 'a-siggraph', name: 'SIGGRAPH Asia', type: 'venue', domain: 'papers-lab' },
      // creators
      { id: 'a-rei', name: 'Rei Sato', type: 'person', domain: 'creators-crm' },
      { id: 'a-elena', name: 'Dr. Elena Frost', type: 'person', domain: 'creators-crm' },
    ],
    edges: [
      // within
      { source: 'a-aster', target: 'a-lumen', type: 'produced by' },
      { source: 'a-aster', target: 'a-saga', type: 'in franchise' },
      { source: 'a-kaito', target: 'a-nocturne', type: 'composed' },
      { source: 'a-paper', target: 'a-siggraph', type: 'published at' },
      { source: 'a-elena', target: 'a-rei', type: 'follows' },
      // ── cross-domain threads (the connected world) ──
      { source: 'a-rei', target: 'a-aster', type: 'directed' }, // creators → anime
      { source: 'a-kaito', target: 'a-aster', type: 'scored' }, // music → anime
      { source: 'a-aster', target: 'a-theme', type: 'scored by' }, // anime → music
      { source: 'a-paper', target: 'a-lumen', type: 'studies' }, // papers → anime
      { source: 'a-elena', target: 'a-paper', type: 'authored' }, // creators → papers
      { source: 'a-ego', target: 'a-aster', type: 'follows' }, // creators → anime
      { source: 'a-ego', target: 'a-kaito', type: 'follows' }, // creators → music
    ],
  },
  signals: [
    {
      id: 'as1',
      title: 'Kaito Mori scored the new Aster Field arc — Music touches your Anime shelf.',
      domain: 'music-desk',
      importance: 'high',
      suggested_action: 'Log the score credit against Aster Field.',
    },
    {
      id: 'as2',
      title: 'Animating Emotion cites Studio Lumen — a paper points at your studio.',
      domain: 'papers-lab',
      importance: 'medium',
      suggested_action: 'Open a mission to brief the affect-animation thread.',
    },
  ],
}

/** The picker menu used when the endpoint is unreachable — one summary per fallback world. */
export const DEMO_STUB_WORLDS: DemoWorldSummary[] = [
  { key: 'ravi', persona: DEMO_STUB.persona },
  { key: 'ana', persona: DEMO_STUB_ANA.persona },
]

/** The fallback world for a key when the endpoint is unreachable — null when this build has
 *  no stub for that key, so the page never shows one world under another's name. */
export function demoStub(key: string): DemoWorld | null {
  if (key === 'ravi') return DEMO_STUB
  if (key === 'ana') return DEMO_STUB_ANA
  return null
}
